import { useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  CREATE_PARTNER_CONTACT,
  UPDATE_PARTNER_CONTACT,
} from '@retainerclub/admin-api';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { BaseLoader, ErrorMessage, SubmitButton } from '../../shared';
import { initialValues, schema } from './helpers';

function PartnerContactFormWrapper({
  partnerContact = null,
  handleCloseModal,
  children,
  partnerOrganizationId,
  partnerLocationId = null,
}) {
  const [mutationErrors, setMutationErrors] = useState(null);

  const [update, { error, loading }] = useMutation(UPDATE_PARTNER_CONTACT);
  const [create, { error: createError, loading: createLoading }] = useMutation(
    CREATE_PARTNER_CONTACT,
  );

  const memoizedInitialValues = useMemo(
    () =>
      initialValues({
        record: { ...partnerContact, partnerOrganizationId, partnerLocationId },
      }),
    [partnerContact, partnerOrganizationId, partnerLocationId],
  );

  const {
    register,
    handleSubmit,
    formState: { isLoading, isSubmitting, isValid },
    formState,
    getValues,
    setValue,
    control,
    ...rest
  } = useForm({
    resolver: yupResolver(schema()),
    defaultValues: memoizedInitialValues,
    values: memoizedInitialValues,
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  async function submitRecord(values) {
    try {
      const params = {
        variables: {
          input: {
            input: { ...values },
            clientMutationId: crypto.randomUUID(),
          },
        },
      };

      if (partnerContact) {
        const { data } = await update(params);

        const mErrors = data.updatePartnerContact.errorMessages;

        if (mErrors.length > 0) {
          setMutationErrors(mErrors);
        } else {
          setMutationErrors(null);
          handleCloseModal();
        }
      } else {
        const { data } = await create(params);

        const mErrors = data.createPartnerContact.errorMessages;

        if (mErrors.length > 0) {
          setMutationErrors(mErrors);
        } else {
          setMutationErrors(null);
          handleCloseModal();
        }
      }
    } catch (err) {
      console.error(err);
    }
  }

  if (loading || createLoading) {
    return <BaseLoader title="Partner Contact" />;
  }

  return (
    <FormProvider
      {...rest}
      control={control}
      register={register}
      handleSubmit={handleSubmit}
      formState={formState}
      getValues={getValues}
      setValue={setValue}
    >
      <form
        onSubmit={handleSubmit(submitRecord)}
        noValidate
        className="flex min-h-[460px] flex-col"
      >
        <div className="grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-6">
          <div className="col-span-6 grid grid-cols-1 gap-4 sm:grid-cols-6 md:col-span-6">
            {children}
          </div>
        </div>

        <div className="mt-auto border-rcgray-600/30 border-t pt-4">
          {error && (
            <div className="mt-4">
              <ErrorMessage error={error} />
            </div>
          )}

          {createError && (
            <div className="mt-4">
              <ErrorMessage error={createError} />
            </div>
          )}

          {mutationErrors && (
            <div className="mt-4">
              <ErrorMessage errorMessages={mutationErrors} />
            </div>
          )}

          <div className="mt-4 flex items-center justify-end gap-x-4">
            <SubmitButton
              isLoading={isLoading || loading || createLoading}
              isSubmitting={isSubmitting}
              isValid={isValid}
              label={partnerContact ? 'Update' : 'Create'}
              full
              notRounded
            />
          </div>
        </div>
      </form>
    </FormProvider>
  );
}

PartnerContactFormWrapper.propTypes = {
  children: PropTypes.node.isRequired,
  handleCloseModal: PropTypes.func.isRequired,
  partnerContact: PropTypes.object,
  partnerOrganizationId: PropTypes.string.isRequired,
  partnerLocationId: PropTypes.string,
};

export default PartnerContactFormWrapper;
